import { useCallback, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { layout, space } from "@tokens/index";
import { Text } from "@primitives/index";
import { EntityWorkflowList } from "@compositions/index";
import type { WorkflowSummary } from "./workflows";
import { useWorkflowsByEntity } from "./workflows";
import { WorkflowRowPresence } from "./realtime/presence";
import { ShellFrame } from "./workflow-layout/ShellFrame";
import { TopBar } from "./workflow-layout/TopBar";
import { BackToWorkflows, ChromeActions } from "./workflow-layout/ChromeActions";

// ---------------------------------------------------------------------------
// Entity-pivoted listing. Its own page area, distinct from the workflow
// workspace, on the shared shell chrome with a back action and a context
// title (entity type + id).
// ---------------------------------------------------------------------------

const pageInnerStyle: CSSProperties = {
    width: "100%",
    maxWidth: layout.contentMaxWidth,
    marginInline: "auto",
    display: "flex",
    flexDirection: "column",
    gap: space[6],
    minWidth: 0,
};

export function EntityWorkflowsPage(): ReactElement {
    const navigate = useNavigate();
    const params = useParams<{ entityType: string; entityId: string }>();

    // The router pattern enforces both segments, but `useParams` types them
    // as optional. Empty values keep the query disabled and show a guardrail
    // message instead of a request that is destined to 400.
    const entityType = params.entityType ?? "";
    const entityId = params.entityId ?? "";

    const { data, isLoading, error } = useWorkflowsByEntity(entityType, entityId);
    const items = data?.items ?? [];

    // Workflows share the entity, so any populated display name is canonical
    // for the partition.
    const entityDisplayName =
        items.find((w) => w.entityDisplayName.length > 0)?.entityDisplayName ?? "";

    const handleSelect = useCallback(
        (item: WorkflowSummary) => {
            const search = new URLSearchParams({
                entityType: item.entityType,
                entityId: item.entityId,
            });
            void navigate(`/workflows/${item.id}?${search.toString()}`);
        },
        [navigate],
    );

    const contextTitle = entityType.length > 0 && entityId.length > 0 ? `${entityType} · ${entityId}` : "";

    if (entityType.length === 0 || entityId.length === 0) {
        return (
            <Chrome contextTitle={contextTitle}>
                <Text role="title" size="xl">
                    Missing entity coordinates
                </Text>
                <Text role="bodySm" tone="muted">
                    The URL must include both an entity type and id, e.g.{" "}
                    <Text as="span" role="mono" size="sm">
                        /entities/load/789
                    </Text>
                    .
                </Text>
            </Chrome>
        );
    }

    // exactOptionalPropertyTypes: only forward `error` when it is a real
    // Error so we do not pass `undefined` into a slot typed `Error | null`.
    const errorProp =
        error !== null && error !== undefined ? { error: error as Error } : {};

    return (
        <Chrome contextTitle={contextTitle}>
            <EntityWorkflowList
                entityType={entityType}
                entityId={entityId}
                entityDisplayName={entityDisplayName}
                items={items}
                isLoading={isLoading}
                onSelect={handleSelect}
                renderPresence={(item) => <WorkflowRowPresence workflowId={item.id} />}
                {...errorProp}
            />
        </Chrome>
    );
}

interface ChromeProps {
    readonly contextTitle: string;
    readonly children: ReactNode;
}

function Chrome({ contextTitle, children }: ChromeProps): ReactElement {
    return (
        <ShellFrame
            topBar={
                <TopBar
                    leading={<BackToWorkflows />}
                    contextTitle={contextTitle}
                    actions={<ChromeActions />}
                />
            }
            content="scroll"
        >
            <div style={pageInnerStyle}>{children}</div>
        </ShellFrame>
    );
}
